/**
 * API Configuration
 *
 * Centralized API key management for external services.
 * All keys must be provided via environment variables.
 */

// Earthscale dynamic-raster tile configuration for a single data layer.
// `rescale` is applied server-side to map raw raster values into the 0–255
// colormap range. `bbox` is [west, south, east, north] in lng/lat — used to
// decide which state datasets to pull when a supply shed crosses state lines.
// `maxZoom` caps how far the map may zoom in while the layer is active — set
// to the dataset's native tile-pyramid depth so users don't zoom past the
// source resolution into blurry/upscaled tiles.
export interface EarthscaleLayerConfig {
  datasetVersionId: string;
  variable: string;
  rescale: [number, number];
  bbox: [number, number, number, number];
  maxZoom: number;
}

// Per-layer cache-busting vintage tags. The S3 objects don't set explicit
// Cache-Control headers, so browsers fall back to heuristic caching that
// can outlive a hard refresh in some sessions. Appending `?v=YYYYMMDD` to
// the URL changes the URL identity that all caching layers key off of:
//   - browsers treat `file.png?v=20260427` as a different resource from
//     `file.png?v=20260426`, forcing a fresh network fetch.
//   - CloudFront (the CDN in front of S3) respects query strings as part
//     of the cache key by default, so each vintage gets its own edge cache
//     entry (no purge required when bumping).
//   - S3 itself is the origin and just serves the underlying object —
//     query strings have no effect on what byte stream comes back.
// Bump the relevant vintage whenever the precompute output for that layer
// is re-uploaded.
const SD_DATA_VINTAGE = '20260426';
// 20260427 — current deploy: PNG overlays generated by the WHP × RD
// decision matrix in scripts/generate-resilience-priority-overlay.py
// (mirrors RESILIENCE_PRIORITY_MATRIX in lib/resiliencePriorityColors.ts).
// To change the matrix in the future: edit both copies, bump this vintage,
// re-run the script, and `aws s3 sync output/scenarios/ s3://ctrees-website/sfi/scenarios/`.
const RESILIENCE_PRIORITY_VINTAGE = '20260427';
// Per-Crown Forest Attributes (Sierra Nevada v1.2). Bump whenever
// scripts/generate-per-crown-data.py re-runs and outputs are synced to
// s3://ctrees-website/sfi/per_crown/.
const PER_CROWN_VINTAGE = '20260430';
// AGB change product — smoothed-endpoint diff (mean 2021-25 minus mean
// 2000-04), 5-bin diverging PNG + per-ownership crosstab CSV produced by
// scripts/generate-agb-change-data.py. Bump when AGB_CHANGE_BIN_EDGES,
// AGB_CHANGE_PALETTE, or the smoothing windows in lib/agbChangeColors.ts
// change and the script is re-run.
const AGB_CHANGE_VINTAGE = '20260430';

export const API_CONFIG = {
  stadiaMaps: {
    apiKey: import.meta.env.VITE_STADIA_MAPS_API_KEY || '',
  },
  earthscale: {
    // Key is embedded directly in the tile URL rather than read from env, so
    // the layer renders without any local setup. Restrict by HTTP referrer in
    // the Earthscale console to keep the exposure bounded. Rotate here if the
    // key ever needs cycling.
    apiKey: '8i6CeLTgACKk2ljOflP4J8X6gu9Rfc6v',
    // Regional "fast" GCP endpoint — CDN-fronted, noticeably quicker than
    // the canonical api.earthscale.ai host. Same path structure.
    tileHost: 'https://fast.gcp.earthscale.ai/us-central1',
    // Per-state dataset mapping. One Earthscale dataset per state. Rescale
    // bounds (in meters) vary by state because the max canopy height differs
    // regionally — keeping them per-state makes the viridis ramp span the
    // full observed range in each shed. States not listed fall through to the
    // "not yet available" placeholder in CanopyHeightContent.
    canopyHeight: {
      California: {
        datasetVersionId: 'b785f787-c783-42fc-8dad-5fd2e97e0696',
        variable: 'band_1',
        rescale: [0, 78],
        bbox: [-124.48, 32.53, -114.13, 42.01],
        // 5 m source data — at z=15 one web-mercator tile pixel is ~5 m at
        // these latitudes, so z=15 is the natural native cap.
        maxZoom: 15,
      },
      Oregon: {
        datasetVersionId: '187abf75-d2df-4599-8707-3897b2dcfbb6',
        variable: 'band_1',
        rescale: [0, 77],
        bbox: [-124.57, 41.99, -116.46, 46.29],
        maxZoom: 15,
      },
      Alabama: {
        datasetVersionId: '0e7b72cc-47a3-4f6f-abdf-21a17eaa53dc',
        variable: 'band_1',
        rescale: [0, 60],
        bbox: [-88.47, 30.14, -84.89, 35.01],
        maxZoom: 15,
      },
      Georgia: {
        datasetVersionId: '23d899fe-6f43-4bce-96b7-db4144909b07',
        variable: 'band_1',
        rescale: [0, 50],
        bbox: [-85.61, 30.36, -80.75, 35.00],
        maxZoom: 15,
      },
    } as Record<string, EarthscaleLayerConfig | undefined>,
  },
  // AGB year-slider overlay — reads precomputed tile pyramid from S3
  // (scripts/scrape-agb-overlay-tiles.py bakes 26 years × z=5..10 from
  // Flux into static PNGs). Replaces a runtime Flux fetch that cost
  // ~800-2400 ms per tile with a CDN GET that costs ~200-600 ms and
  // gets browser-cached for 30 days. No auth — public S3 bucket.
  agbOverlay: {
    minYear: 2000,
    maxYear: 2025,
    // Lower bound of the precomputed pyramid. Flux rejects z<5 with HTTP
    // 413 ("too big") because the 100 m source would require too many
    // pixels per tile at wide zooms, so the scrape pyramid starts at 5.
    minZoom: 5,
    // Top of the precomputed pyramid. The 100 m source is already
    // oversampled past z=10, so we don't bake further tiers — but we no
    // longer hard-cap zoom-in here. Plumbed into Leaflet as
    // `maxNativeZoom` so it upscales the z=10 tiles past z=10 (pixelated
    // AGB over a higher-detail basemap) instead of 404-ing on tiles
    // that don't exist in the pyramid.
    maxNativeZoom: 10,
    // Legend domain used by AgbMapLegend — approximate, since Flux's
    // default rescale isn't published. Matches the labels on the
    // precomputed basemap legend so the two legends agree.
    legendRescale: [0, 500] as [number, number],
  },
  // Earthmover Flux serves dynamic time-series rasters from the CTrees
  // Arraylake catalog. Routes by path: `/v1/services/tiles/<org>/<dataset>/
  // <branch>/<variable>`. Authenticates with HTTP Basic (username fixed to
  // `ctrees`, password from VITE_EARTHMOVER_TOKEN). Token ships in the
  // bundle, same demo-window tradeoff as the Earthscale key — restrict by
  // referrer before any public production deploy. Currently used only by
  // soil carbon (the AGB overlay migrated to a precomputed S3 pyramid; see
  // `agbOverlay` above).
  earthmoverFlux: {
    tileHost: 'https://compute.earthmover.io',
    username: 'ctrees',
    token: import.meta.env.VITE_EARTHMOVER_TOKEN || '',
    // Soil organic carbon (2022 snapshot) served as two sibling datasets —
    // one per depth profile. The CSV stats hook picks depth at read time; the
    // overlay hook picks the matching tile pyramid. `colorscalerange` is
    // depth-specific because the absolute carbon stock range differs
    // substantially between the topsoil (0–30 cm, ~0–250 Mg C/ha) and the
    // full profile (0–100 cm, ~0–650 Mg C/ha).
    soilCarbon: {
      variable: 'measurement',
      style: 'raster/gist_earth',
      // Same 100 m source as AGB. The Flux endpoint 404s past z=10
      // because there's no pre-baked S3 pyramid for SOC (unlike AGB).
      // Plumbed into Leaflet as `maxNativeZoom` so it caps tile fetches
      // at z=10 and upscales the z=10 tiles client-side past z=10
      // (pixelated, but the user can keep zooming for any layer stacked
      // on top — same shape as the AGB unlock). When/if a SOC scrape
      // pyramid is baked, lift this restriction the same way AGB did.
      maxNativeZoom: 10,
      depths: {
        30: {
          datasetPath:
            '/v1/services/tiles/ctrees/soil_100m_global/2026_update/30cm/tiles/WebMercatorQuad',
          colorscalerange: [0, 250] as [number, number],
          legendRescale: [0, 250] as [number, number],
        },
        100: {
          datasetPath:
            '/v1/services/tiles/ctrees/soil_100m_global/2026_update/100cm/tiles/WebMercatorQuad',
          colorscalerange: [0, 650] as [number, number],
          legendRescale: [0, 650] as [number, number],
        },
      },
    },
  },
  s3: {
    baseUrl: import.meta.env.VITE_S3_BASE_URL || 'https://viz-assets.ctrees.org/sfi',
    paths: {
      millShape: (mill: string, dt: number) => `mill_shapes/${mill}_${dt}min.geojson`,
      agb: (mill: string, dt: number) => `agb/${mill}_${dt}min_agb.csv`,
      growthCurves: (mill: string, dt: number) => `growth_curves/growth_curves_100m_global_stats_${mill}_${dt}min.csv`,
      soilCarbon: (depth: 30 | 100) => `soc/soil_organic_carbon_stats_${depth}cm.csv`,
      whpStats: (mill: string, dt: number) => `whp/${mill}_${dt}min_whp.csv`,
      whpOverlay: (mill: string, dt: number) => `whp/overlays/${mill}_${dt}min_whp.png`,
      whpOverlayMeta: (mill: string, dt: number) => `whp/overlays/${mill}_${dt}min_whp.meta.json`,
      speciesStats: (mill: string, dt: number) => `species/${mill}_${dt}min_species.csv`,
      speciesOverlayDominant: (mill: string, dt: number) => `species/overlays/${mill}_${dt}min_species_dominant.png`,
      speciesOverlayDominantMeta: (mill: string, dt: number) => `species/overlays/${mill}_${dt}min_species_dominant.meta.json`,
      speciesOverlayRichness: (mill: string, dt: number) => `species/overlays/${mill}_${dt}min_species_richness.png`,
      speciesOverlayRichnessMeta: (mill: string, dt: number) => `species/overlays/${mill}_${dt}min_species_richness.meta.json`,
      sdStats: (mill: string, dt: number) => `stand_density/${mill}_${dt}min_stand_density.csv?v=${SD_DATA_VINTAGE}`,
      sdOverlay: (mill: string, dt: number) => `stand_density/overlays/${mill}_${dt}min_stand_density.png?v=${SD_DATA_VINTAGE}`,
      sdOverlayMeta: (mill: string, dt: number) => `stand_density/overlays/${mill}_${dt}min_stand_density.meta.json?v=${SD_DATA_VINTAGE}`,
      foStats: (mill: string, dt: number) => `forest_ownership/${mill}_${dt}min_forest_ownership.csv`,
      foOverlay: (mill: string, dt: number) => `forest_ownership/overlays/${mill}_${dt}min_forest_ownership.png`,
      foOverlayMeta: (mill: string, dt: number) => `forest_ownership/overlays/${mill}_${dt}min_forest_ownership.meta.json`,
      // Private-timberland focus mask — sibling PNG that paints translucent
      // black over non-private classes so family + corporate stay vibrant on
      // top of the categorical overlay during scenario steps. Bounds match
      // the categorical PNG, so the hook reuses foOverlayMeta.
      foPrivateMaskOverlay: (mill: string, dt: number) => `forest_ownership/overlays/${mill}_${dt}min_forest_ownership_private_mask.png`,
      // LUCA producer publishes under `luca/historical/` with an `sfi-` prefix
      // on the mill slug. Four mill codes diverge from the producer's slug —
      // map them here so callers can keep passing the mill `code` and still
      // resolve to the correct key.
      lucaStats: (mill: string, dt: number) => {
        const reversedRegionMapping: Record<string, string> = {
          'georgia-western-piedmont': 'georgia-ridge-and-valley',
          'georgia-inner-coastal-plain': 'georgia-coastal-plain',
          'alabama-fall-line-hills': 'alabama-plains',
          'oregon-south-coast-range': 'oregon-klamath-mountains',
        };
        const mapped = reversedRegionMapping[mill] ?? mill;
        return `luca/historical/luca_sfi-${mapped}_${dt}min_zonal_stats_stacked.csv`;
      },
      mobiStats: (mill: string, dt: number) => `mobi/${mill}_${dt}min_mobi.csv`,
      mobiOverlay: (mill: string, dt: number) => `mobi/overlays/${mill}_${dt}min_mobi.png`,
      mobiOverlayMeta: (mill: string, dt: number) => `mobi/overlays/${mill}_${dt}min_mobi.meta.json`,
      // AGB year-slider overlay: precomputed tile pyramid scraped from
      // Earthmover Flux (see scripts/scrape-agb-overlay-tiles.py). The
      // returned template still has Leaflet's `{z}/{x}/{y}` placeholders,
      // which Leaflet fills at request time.
      agbOverlayTile: (year: number) => `agb/tiles/${year}/{z}/{x}/{y}.png`,
      // AGB change product — single PNG of the smoothed-endpoint difference
      // (mean 2021-25 minus mean 2000-04), 5-bin diverging palette, clipped
      // to shed. Used by the redesigned Carbon Offense scenario when the
      // step's `overlayVariants.aboveground_biomass === 'change'`.
      agbChangeOverlay: (mill: string, dt: number) => `agb/change/${mill}_${dt}min_change.png?v=${AGB_CHANGE_VINTAGE}`,
      agbChangeOverlayMeta: (mill: string, dt: number) => `agb/change/${mill}_${dt}min_change.meta.json?v=${AGB_CHANGE_VINTAGE}`,
      // Per-ownership crosstab — long-format CSV with one row per
      // ownership_class plus an `all_private` rollup row. Drives the
      // Carbon Offense maintenance insight card.
      agbChangeByOwnership: (mill: string, dt: number) => `agb/change/${mill}_${dt}min_change_by_ownership.csv?v=${AGB_CHANGE_VINTAGE}`,
      // Scenario Mode — per-(mill, drive-time) joint crosstabs for derived
      // insight cards. Currently only Scenario 2's resilience matrix is
      // scoped; add further scenarios' CSVs here as precompute scripts land.
      resilienceMatrix: (mill: string, dt: number) => `scenarios/${mill}_${dt}min_resilience.csv`,
      // Resilience Priority synthesis overlay — 5-tier priority spectrum
      // painted on private timberland. Each pixel's tier is assigned via
      // the documented WHP × RD decision matrix in
      // lib/resiliencePriorityColors.ts. Used by the Carbon Defense
      // scenario's final step and selectable from the layer dropdown for
      // free exploration. The vintage suffix busts browser cache whenever
      // the precompute is re-run.
      resiliencePriorityOverlay: (mill: string, dt: number) => `scenarios/overlays/${mill}_${dt}min_resilience_priority.png?v=${RESILIENCE_PRIORITY_VINTAGE}`,
      resiliencePriorityOverlayMeta: (mill: string, dt: number) => `scenarios/overlays/${mill}_${dt}min_resilience_priority.meta.json?v=${RESILIENCE_PRIORITY_VINTAGE}`,
      // Per-Crown Forest Attributes — NAIP-derived crown polygons aggregated
      // into per-shed stats (CSV), a coarse-grid Total Tree Biomass density
      // PNG overlay, and a per-tree insight rollup JSON. Coverage v1.2 is
      // limited to the Sierra Nevada 60-min shed; other (mill, dt) tuples
      // 404 cleanly and the layer falls back to a "no coverage" panel.
      perCrownStats: (mill: string, dt: number) => `per_crown/${mill}_${dt}min_per_crown.csv?v=${PER_CROWN_VINTAGE}`,
      perCrownInsight: (mill: string, dt: number) => `per_crown/${mill}_${dt}min_per_tree_rollup.json?v=${PER_CROWN_VINTAGE}`,
      // Native-resolution demo window — small static GeoJSON of the actual
      // crown polygons in the densest 1 km² of the shed. Gated behind an
      // explicit "View individual trees" button + camera fly in the right
      // panel; not loaded at all when the layer is inactive.
      perCrownNative: (mill: string, dt: number) => `per_crown/${mill}_${dt}min_per_crown_native.geojson?v=${PER_CROWN_VINTAGE}`,
    },
  },
} as const;
